/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import 'eyedropper-polyfill';

import { tokens } from '@neo4j-ndl/base';
import {
  type HexColor,
  hexToHsva,
  type HsvaColor,
  hsvaToHex,
  hsvaToRgba,
  Hue,
  type PointerProps,
  type RgbaColor,
  rgbaToHex,
  rgbaToHsva,
  Saturation,
  validHex,
} from '@uiw/react-color';
import classNames from 'classnames';
import { forwardRef, useEffect, useMemo, useState } from 'react';

import { CleanIconButton } from '../clean-icon-button';
import { EyeDropperIconOutline } from '../icons/generated/heroIcons/EyeDropperIconOutline';
import { Select } from '../select';
import { TextInput } from '../text-input';

/**
 * @remarks
 * By default the `swatches` prop is set to the available graph colors (`tokens.graph`) from `@neo4j-ndl/base`.
 *
 * @remarks
 * Where `HsvaColor`, `RgbaColor`, and `HexColor` types are from `@uiw/react-color`, and represent different color formats:
 *
 * - `HexColor`: A string in the format `#RRGGBB` (e.g., `#ff0000`).
 * - `RgbaColor`: An object with numeric properties `{ r, g, b, a }` for red, green, blue (0–255), and alpha (0–1).
 * - `HsvaColor`: An object with numeric properties `{ h, s, v, a }` for hue (0–360), saturation (0–100), value (0–100), and alpha (0–1).
 *
 * Visit [react-color](https://uiwjs.github.io/react-color/#/) for more information.
 */
type ColorPickerProps = {
  /** The current color value. Can be provided in HSVA, RGBA, or Hex format. The component will automatically convert between formats as needed. */
  color: HsvaColor | RgbaColor | HexColor;
  /** Optional array of predefined color swatches for quick selection. Each swatch can be in HSVA, RGBA, or Hex format. */
  swatches?: (HsvaColor | RgbaColor | HexColor)[];
  /** Whether to display the eye dropper tool that allows users to sample colors from anywhere on the screen. */
  shouldShowEyeDropper?: boolean;
  /** Callback function triggered when the color value changes. Receives an object containing the new color in all three formats (hex, rgb, hsva) for convenience. */
  onChange: (newColor: {
    hex: HexColor;
    rgb: RgbaColor;
    hsva: HsvaColor;
  }) => void;
  /** Additional class name to apply to the root color picker container. */
  className?: string;
  /** Additional inline styles to apply to the root color picker container. */
  style?: React.CSSProperties;

  /** The target element to portal the menu to. If not provided, the menu will not be portaled. */
  portalTarget?: HTMLElement | null;
  /** The floating strategy to use for the menu. If not provided, the menu will be positioned absolutely. */
  floatingStrategy?: 'absolute' | 'fixed';
};

export const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
  function ColorPicker(
    {
      color,
      onChange,
      swatches = Object.values(tokens.graph).filter(validHex),
      shouldShowEyeDropper = true,
      className,
      style,
      portalTarget,
      floatingStrategy = 'absolute',
    },
    ref,
  ) {
    const [isEyeDropperActiveState, setIsEyeDropperActiveState] =
      useState(false);

    const derivedHsva = useMemo(() => {
      if (typeof color === 'string') {
        return hexToHsva(color);
      }
      if (typeof color === 'object' && 'r' in color) {
        return rgbaToHsva(color);
      }
      return { h: 0, s: 0, v: 0, ...(color as object), a: 1 } as HsvaColor; // oxlint-disable-line id-length
    }, [color]);

    const [hsva, setHsva] = useState<HsvaColor>(derivedHsva);

    const [format, setFormat] = useState<{
      label: string;
      value: string;
    }>({
      label: 'Hex',
      value: 'hex',
    });

    const isEyeDropperActive = 'EyeDropper' in window && shouldShowEyeDropper;

    const handleChange = (hsva: HsvaColor) => {
      setHsva(hsva);
      const hex = hsvaToHex(hsva);
      const rgb = hsvaToRgba(hsva);
      onChange({
        // used since typescript cannot infer the type of the color
        hex: `#${hex.split('#')[1]}`,
        hsva,
        rgb,
      });
    };

    return (
      <div
        ref={ref}
        className={classNames('ndl-color-picker', className)}
        style={style}
      >
        <Saturation
          hsva={hsva}
          className="ndl-color-picker-saturation"
          onChange={(newColor) => {
            handleChange({ ...hsva, ...newColor, a: hsva.a }); // oxlint-disable-line id-length
          }}
          pointer={({ left, top, ...props }) => (
            <Pointer
              left={left?.toString()}
              top={top?.toString()}
              {...props}
              hsva={hsva}
            />
          )}
          radius={tokens.borderRadius['lg']}
        />
        <Swatch
          colors={swatches}
          hsva={hsva}
          onChange={(newColor) => {
            handleChange(newColor);
          }}
        />

        <div className="ndl-color-picker-hue-container">
          {isEyeDropperActive && (
            <CleanIconButton
              size="small"
              isActive={isEyeDropperActiveState}
              onClick={() => {
                setIsEyeDropperActiveState(true);
                // TODO: Remove the any casting when polyfiller is not needed anymore. https://developer.mozilla.org/en-US/docs/Web/API/EyeDropper
                // Type assertion to handle the unknown type
                // oxlint-disable-next-line @typescript-eslint/no-explicit-any
                const eyeDropper = new (window as any).EyeDropper();
                eyeDropper
                  .open()
                  .then((result: { sRGBHex: string }) => {
                    handleChange(hexToHsva(result.sRGBHex));
                  })
                  .catch((err: Error) => {
                    console.error(err);
                  })
                  .finally(() => {
                    setIsEyeDropperActiveState(false);
                  });
              }}
              description="Pick color from the screen with eye dropper tool"
            >
              <EyeDropperIconOutline />
            </CleanIconButton>
          )}
          <Hue
            className="ndl-color-picker-hue"
            hue={hsva.h}
            onChange={(newHue) => {
              handleChange({ ...hsva, h: newHue.h }); // oxlint-disable-line id-length
            }}
            radius={tokens.borderRadius['lg']}
            pointer={(props) => (
              <Pointer
                {...props}
                hsva={{
                  a: 1, // oxlint-disable-line id-length
                  h: hsva.h, // oxlint-disable-line id-length
                  s: 100, // oxlint-disable-line id-length
                  v: 100, // oxlint-disable-line id-length
                }}
              />
            )}
          />
        </div>

        <div className="ndl-color-picker-inputs">
          <Select
            size="small"
            type="select"
            ariaLabel="Color format"
            style={{
              flexShrink: 0,
            }}
            selectProps={{
              isSearchable: false,
              menuPortalTarget: portalTarget,
              menuPosition: floatingStrategy,
              onChange: (newFormat) => {
                if (!newFormat) {
                  return;
                }
                setFormat({
                  label: newFormat.label,
                  value: newFormat.value,
                });
              },
              options: [
                {
                  label: 'Hex',
                  value: 'hex',
                },
                {
                  label: 'RGB',
                  value: 'rgb',
                },
              ],
              value: { label: format.label, value: format.value },
            }}
          />
          {format.value === 'hex' && (
            <HexInput
              hsva={hsva}
              onChange={(newColor) => {
                handleChange(hexToHsva(newColor));
              }}
            />
          )}
          {format.value === 'rgb' && (
            <RgbInput
              hsva={hsva}
              onChange={(newColor) => {
                handleChange(hexToHsva(newColor));
              }}
            />
          )}
        </div>
      </div>
    );
  },
);

const removeHashPrefix = (hex: string): string => hex.replace('#', '');

const HexInput = ({
  hsva,
  onChange,
}: {
  hsva: HsvaColor;
  onChange: (newColor: string) => void;
}) => {
  const [inputValue, setInputValue] = useState<string>(() =>
    removeHashPrefix(hsvaToHex(hsva)),
  );

  // Update input value when hsva changes from outside
  useEffect(() => {
    setInputValue(removeHashPrefix(hsvaToHex(hsva)));
  }, [hsva]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = removeHashPrefix(event.target.value);

    // Always update the input value for immediate feedback
    setInputValue(value);

    // Check if the input is a valid hex color (only 6 characters)
    const isValidHex = /^[0-9A-Fa-f]{6}$/.test(value);

    if (isValidHex) {
      onChange(`#${value}`);
    }
  };

  const hexValue = hsvaToHex(hsva);

  return (
    <TextInput
      size="small"
      value={inputValue}
      leadingElement={
        <div className="ndl-color-picker-hex-input-prefix">#</div>
      }
      onChange={handleChange}
      isFluid
      htmlAttributes={{
        'aria-label': 'Hex color code',
        maxLength: 6,
        onCopy: (event) => {
          event.preventDefault();
          navigator.clipboard.writeText(hexValue);
        },
      }}
    />
  );
};

const RgbInput = ({
  hsva,
  onChange,
}: {
  hsva: HsvaColor;
  onChange: (newColor: string) => void;
}) => {
  const rgba = hsvaToRgba(hsva);
  const [rgbValues, setRgbValues] = useState({
    b: rgba.b, // oxlint-disable-line id-length
    g: rgba.g, // oxlint-disable-line id-length
    r: rgba.r, // oxlint-disable-line id-length
  });

  // Update input values when hsva changes from outside
  useEffect(() => {
    const newRgba = hsvaToRgba(hsva);
    setRgbValues({
      b: newRgba.b, // oxlint-disable-line id-length
      g: newRgba.g, // oxlint-disable-line id-length
      r: newRgba.r, // oxlint-disable-line id-length
    });
  }, [hsva]);

  const handleChange = (
    channel: 'r' | 'g' | 'b',
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseInt(event.target.value, 10);

    // Validate the input is a number between 0-255
    const validValue = isNaN(value) ? 0 : Math.max(0, Math.min(255, value));

    const newRgbValues = {
      ...rgbValues,
      [channel]: validValue,
    };

    setRgbValues(newRgbValues);

    // Convert RGB to hex and call onChange
    const hexColor = `#${newRgbValues.r.toString(16).padStart(2, '0')}${newRgbValues.g.toString(16).padStart(2, '0')}${newRgbValues.b.toString(16).padStart(2, '0')}`;
    onChange(hexColor);
  };

  return (
    <div className="ndl-color-picker-rgb-inputs">
      <TextInput
        size="small"
        className="ndl-color-picker-rgb-input"
        value={rgbValues.r.toString()}
        onChange={(event) => handleChange('r', event)}
        htmlAttributes={{
          'aria-label': 'Red',
          max: '255',
          min: '0',
          type: 'number',
        }}
      />
      <TextInput
        size="small"
        className="ndl-color-picker-rgb-input"
        value={rgbValues.g.toString()}
        onChange={(event) => handleChange('g', event)}
        htmlAttributes={{
          'aria-label': 'Green',
          max: '255',
          min: '0',
          type: 'number',
        }}
      />
      <TextInput
        size="small"
        className="ndl-color-picker-rgb-input"
        value={rgbValues.b.toString()}
        onChange={(event) => handleChange('b', event)}
        htmlAttributes={{
          'aria-label': 'Blue',
          max: '255',
          min: '0',
          type: 'number',
        }}
      />
    </div>
  );
};

const Swatch = ({
  colors,
  onChange,
  hsva,
}: {
  colors: (HsvaColor | RgbaColor | HexColor)[];
  hsva: HsvaColor;
  onChange: (newColor: HsvaColor) => void;
}) => {
  return (
    <div className="ndl-color-picker-swatch">
      {colors.map((color) => {
        const hexColor =
          typeof color === 'string'
            ? color
            : typeof color === 'object' && 'r' in color
              ? rgbaToHex(color)
              : hsvaToHex(color);
        const hsvaColor =
          typeof color === 'string'
            ? hexToHsva(color)
            : 'r' in color
              ? rgbaToHsva(color)
              : color;

        const isActive =
          hsva.h === hsvaColor.h &&
          hsva.s === hsvaColor.s &&
          hsva.v === hsvaColor.v;

        return (
          <button
            type="button"
            key={hexColor}
            aria-label={hexColor}
            className={classNames('ndl-color-picker-swatch-color', {
              'ndl-color-picker-swatch-color-active': isActive,
            })}
            style={{ backgroundColor: hsvaToHex(hsvaColor) }}
            onClick={() => onChange(hsvaColor)}
          />
        );
      })}
    </div>
  );
};
const Pointer = ({
  prefixCls,
  left,
  top,
  hsva,
}: PointerProps & { hsva: HsvaColor }) => {
  const rgba = hsvaToRgba(hsva);
  return (
    <div
      style={{
        backgroundColor: `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`,
        left,
        top,
      }}
      className={classNames(prefixCls, 'ndl-color-picker-pointer')}
    />
  );
};
